import React, { useEffect, useState, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import SwayWhiteLogo from './SwayWhiteLogo.png';
import SwayLogo from './SwayPrdIcon.jpeg';
import AppleMusicIcon from './apple-music-app-icon.jpeg';
import InstagramLogo from './InstagramWhiteLogo.png';
import XBlackLogo from './XWhiteLogo.png';
import Song from './Song.png';
import Link from './LinkWhiteLogo.png';
import {API_BASE_URL} from '../Api';
import { useNoIndex } from '../seo/useNoIndex';

interface InvitationPageProps {}

interface InvitationParams {
  userNumber?: string;
  invitationCode?: string;
  [key: string]: string | undefined;
}

interface LocationInfo {
  city: string;
  prefecture: string;
  country: string;
}

interface NowPlaying {
  track_title: string;
  track_artwork_url: string;
  track_artist: string;
}

interface Track {
  track_title: string;
  track_artist: string;
  track_album?: string;
  track_artwork_url: string;
  apple_music_id?: string;
  spotify_url?: string;
  created_at?: string | number | { seconds: number; nanoseconds?: number };
  latitude?: number | null;
  longitude?: number | null;
  geohash?: string | null;
}

interface Playlist {
  title: string;
  platform: string;
  share_url: string;
  artwork_url: string | null;
  overview?: string;
  top_tracks?: Track[];
}

interface SnsLink {
  sns_id: string;
  sns_user_id: string;
  is_authed: boolean;
}

interface InviterInfoResponse {
  status: string;
  nickname: string;
  medium_icon_url: string | null;
  user_number?: number | null;
  favorite_place_name: string | null;
  now_playing: NowPlaying | null;
  recently_played: Track[];
  favorite_songs: Track[];
  invite_code?: string;
  playlists: Playlist[];
  cross_count?: number | null;
  react_count?: number | null;
  follower_count?: number | null;
  favorite_place_geohash?: string | null;
  mbti?: string | null;
  birthday?: string | null;
  sns_links?: SnsLink[];
}

interface ReverseGeocodeResult {
  label: string | null;
  city?: string;
  prefecture?: string;
  country?: string;
}

type ErrorKind = 'invalid' | 'network' | null;

/** 招待者が解決できなかったことを表す。リトライしても直らないので即座に諦める。 */
class InviterNotFoundError extends Error {
  constructor(public readonly detail: string) {
    super(detail);
    this.name = 'InviterNotFoundError';
  }
}

const FETCH_TIMEOUT_MS = 15000;
const RETRY_DELAYS_MS = [500, 1500];

/**
 * 招待者情報を取得する。Render の cold start で一時的に落ちることがあるため、
 * ネットワークエラーと 5xx のときだけ指数バックオフでリトライする。
 * 404 (招待者が居ない/コードが無効) はリトライしても同じなので即座に投げる。
 */
const fetchInviterInfo = async (
  queryParams: string,
  signal: AbortSignal
): Promise<InviterInfoResponse> => {
  let lastError: Error = new Error('不明なエラー');

  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    if (attempt > 0) {
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAYS_MS[attempt - 1]));
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
    }

    const timeoutController = new AbortController();
    const timeoutId = setTimeout(() => timeoutController.abort(), FETCH_TIMEOUT_MS);
    const onOuterAbort = () => timeoutController.abort();
    signal.addEventListener('abort', onOuterAbort);

    try {
      const response = await fetch(`${API_BASE_URL}/invitation/get_nickname/?${queryParams}`, {
        signal: timeoutController.signal,
      });

      if (response.status >= 400 && response.status < 500) {
        // サーバーが明示した原因コードがあれば拾う (invalid_user / invalid_code)
        const body = await response.json().catch(() => null);
        throw new InviterNotFoundError(body?.code || `http_${response.status}`);
      }

      if (!response.ok) {
        throw new Error(`http_${response.status}`);
      }

      return await response.json();
    } catch (error) {
      if (error instanceof InviterNotFoundError) throw error;
      // 外側の abort (アンマウント/パラメータ変更) は呼び出し元に伝える
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');

      lastError =
        error instanceof DOMException && error.name === 'AbortError'
          ? new Error('timeout')
          : error instanceof Error
          ? error
          : new Error('network_error');
    } finally {
      clearTimeout(timeoutId);
      signal.removeEventListener('abort', onOuterAbort);
    }
  }

  throw lastError;
};

const InvitationPage: React.FC<InvitationPageProps> = () => {
  useNoIndex();
  const params = useParams<InvitationParams>();
  const location = useLocation();

  const isDarkTheme = true;
  const ACCENT_COLOR = 'rgb(4, 177, 248)';

  const themeColors = {
    primary: '#fff',
    secondary: 'rgba(255, 255, 255, 0.65)',
    faint: 'rgba(255, 255, 255, 0.25)',
    cardBg: 'rgba(255, 255, 255, 0.04)',
    border: 'rgba(255, 255, 255, 0.08)',
    headerBg: 'rgba(10, 10, 10, 0.8)',
    buttonBg: '#ffffff',
    buttonText: '#000000',
    borderupdate: '#ACACAC',
    overlay: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.5) 100%)',
  };

  let invitationCode: string | undefined;
  let userNumber: string | undefined | null;

  if (location.pathname.startsWith('/invitation/')) {
    invitationCode = params.invitationCode;
    userNumber = null;
  } else {
    if (params.userNumber && /^\d+$/.test(params.userNumber)) {
      userNumber = params.userNumber;
      invitationCode = params.invitationCode;
    } else {
      invitationCode = params.userNumber;
      userNumber = null;
    }
  }

  const [copied, setCopied] = useState<boolean>(false);
  const [inviterNickname, setInviterNickname] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [inviterIconUrl, setInviterIconUrl] = useState<string | null>(null);
  const [inviterUserNumber, setInviterUserNumber] = useState<number | null>(null);
  const [locationInfo, setLocationInfo] = useState<LocationInfo | null>(null);
  const [nowPlaying, setNowPlaying] = useState<NowPlaying | null>(null);
  const [locationLoading, setLocationLoading] = useState<boolean>(false);
  const [recentlyPlayed, setRecentlyPlayed] = useState<Track[]>([]);
  const [recentlyPlayedLocations, setRecentlyPlayedLocations] = useState<Record<number, string>>({});
  const [favoriteSongs, setFavoriteSongs] = useState<Track[]>([]);
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [crossCount, setCrossCount] = useState<number | null>(null);
  const [favoritePlaceName, setFavoritePlaceName] = useState<string | null>(null);
  const [favoritePlaceGeohash, setFavoritePlaceGeohash] = useState<string | null>(null);
  const [mbti, setMbti] = useState<string | null>(null);
  const [birthday, setBirthday] = useState<string | null>(null);
  const [snsLinks, setSnsLinks] = useState<SnsLink[]>([]);
  const [showInstallPrompt, setShowInstallPrompt] = useState<boolean>(false);
  const [installPromptMessage, setInstallPromptMessage] = useState<string>('Swayをインストールしましょう！！');
  const [showSafariGuide, setShowSafariGuide] = useState<boolean>(false);
  const [safariGuideCopied, setSafariGuideCopied] = useState<boolean>(false);
  const [reactCount, setReactCount] = useState<number | null>(null);
  const [followerCount, setFollowerCount] = useState<number | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isMountedRef = useRef(true);
  // 'invalid' = 招待者が解決できない(再読み込みしても直らない) / 'network' = 一時的な通信失敗
  const [errorKind, setErrorKind] = useState<ErrorKind>(null);
  // 画面に小さく出す原因コード。報告のスクリーンショット1枚で切り分けるためのもの。
  const [errorDetail, setErrorDetail] = useState<string>('');
  const [resolvedInvitationCode, setResolvedInvitationCode] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState<number>(0);
  const hasLoadedRef = useRef(false);

  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  useEffect(() => {
    // 取得に成功済みなら再取得しない。失敗したときは再試行できるようにしておく。
    if (hasLoadedRef.current) return;

    let isActive = true;
    const abortController = new AbortController();

    const fetchInviterNickName = async () => {
      if (!invitationCode && !userNumber) {
        if (isActive) {
          setIsLoading(false);
          setErrorKind('invalid');
          setErrorDetail('no_code');
        }
        return;
      }
  
      if (invitationCode && isActive) {
        setResolvedInvitationCode(invitationCode);

        if (!userNumber) {         
          fetch(`${API_BASE_URL}/invitation/track_access/`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              invite_code: invitationCode,
              source: 'web',
            }),
          })
          .then((response) => response.json())
          .catch((error) => {
            console.error('🔥 旧形式track_accessエラー:', error);
          });
        }
      }
  
      if (isActive) {
        setIsLoading(true);
        setErrorKind(null);
        setErrorDetail('');
        setInviterNickname('');
        setInviterIconUrl(null);
        setInviterUserNumber(null);
        setNowPlaying(null);
        setRecentlyPlayed([]);
        setRecentlyPlayedLocations({});
        setFavoriteSongs([]);
        setPlaylists([]);
        setCrossCount(null);
        setReactCount(null);
        setFollowerCount(null);
        setLocationInfo(null);
        setFavoritePlaceGeohash(null);
        setMbti(null);
        setBirthday(null);
        setSnsLinks([]);
      }
  
      try {
        let queryParams = '';
        if (invitationCode) {
          queryParams = `invite_code=${encodeURIComponent(invitationCode)}`;
        } else if (userNumber) {
          queryParams = `user_number=${encodeURIComponent(userNumber)}`;
        }

        const data = await fetchInviterInfo(queryParams, abortController.signal);

        if (!isActive) {
          return;
        }

        if (data.status === 'success') {
          hasLoadedRef.current = true;

          setInviterNickname(data.nickname);
          setInviterIconUrl(data.medium_icon_url);
          setInviterUserNumber(data.user_number || null);
          setNowPlaying(data.now_playing);
          setRecentlyPlayed(data.recently_played || []);
          setFavoriteSongs(data.favorite_songs || []);
          setPlaylists(data.playlists || []);
          setCrossCount(data.cross_count || null);
          setReactCount(data.react_count || null);
          setFollowerCount(data.follower_count || null);
          setFavoritePlaceName(data.favorite_place_name);
          setFavoritePlaceGeohash(data.favorite_place_geohash || null);
          setMbti(data.mbti || null);
          setBirthday(data.birthday || null);
          setSnsLinks(data.sns_links || []);
          
          if (userNumber && data.invite_code) {
            setResolvedInvitationCode(data.invite_code);
          
            fetch(`${API_BASE_URL}/invitation/track_access/`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                invite_code: data.invite_code,
                source: 'web',
              }),
            })
            .then((response) => {
              return response.json();
            })
            .catch((error) => {
            });
          }
          
          setErrorKind(null);
          setErrorDetail('');
        } else {
          // HTTP 200 だが status が success ではない = 招待者が解決できない
          setErrorKind('invalid');
          setErrorDetail('invalid_code');
          setInviterNickname('ユーザー');
        }
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') {
          return;
        }
        if (isActive) {
          console.error('招待者情報取得エラー:', error);
          const isInvalid = error instanceof InviterNotFoundError;
          setErrorKind(isInvalid ? 'invalid' : 'network');
          setErrorDetail(error instanceof Error ? error.message : 'unknown_error');
          setInviterNickname('ユーザー');
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };
  
    fetchInviterNickName();
  
    return () => {
      isActive = false;
      abortController.abort();
    };
  }, [invitationCode, userNumber, retryCount]);

  useEffect(() => {
    const controller = new AbortController();
    if (!recentlyPlayed.length) {
      setRecentlyPlayedLocations({});
      return () => controller.abort();
    }

    const fetchLocationLabel = async (index: number, track: Track) => {
      const coordinates = getTrackCoordinates(track);
      if (!coordinates) return;
      try {
        const result = await reverseGeocode(coordinates.latitude, coordinates.longitude, controller.signal);
        if (result?.label && isMountedRef.current) {
          setRecentlyPlayedLocations((prev) => ({ ...prev, [index]: result.label as string }));
        }
      } catch (error) {
        if (!(error instanceof DOMException && error.name === 'AbortError')) {
          console.error('Recently Played地名取得エラー', error);
        }
      }
    };

    setRecentlyPlayedLocations({});
    recentlyPlayed.forEach((track, index) => {
      fetchLocationLabel(index, track);
    });

    return () => controller.abort();
  }, [recentlyPlayed]);

  useEffect(() => {
    if (!favoritePlaceGeohash) return undefined;
    const controller = new AbortController();

    const fetchFavoriteLocation = async () => {
      setLocationLoading(true);
      try {
        const locationResult = await getLocationFromGeohash(favoritePlaceGeohash, controller.signal);
        if (locationResult && isMountedRef.current) {
          setLocationInfo(locationResult);
        }
      } finally {
        if (isMountedRef.current) setLocationLoading(false);
      }
    };

    fetchFavoriteLocation();

    return () => controller.abort();
  }, [favoritePlaceGeohash]);

  const copyToClipboard = (text: string) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 3000);
        })
        .catch(() => {});
    }
  };

  const handleCodeCopy = () => {
    if (invitationCode) {
      copyToClipboard(invitationCode);
    }
  };

  const DEFAULT_APP_STORE_URL =
    'https://apps.apple.com/jp/app/sway-%E3%82%B9%E3%82%A6%E3%82%A7%E3%82%A4/id6738626019';

  // user_number ごとに App Store の遷移先を切り替える
  const USER_NUMBER_APP_STORE_URLS: Record<string, string> = {
    '32043': // Minori (dummy_user_4db45378)
      'https://apps.apple.com/app/apple-store/id6738626019?pt=127417191&ct=bereal&mt=8',
    '32038': // 麻衣やん (dummy_user_cebae4f1)
      'https://apps.apple.com/app/apple-store/id6738626019?pt=127417191&ct=music_3_select&mt=8',
    '6056': // たけぞう (UqfivsQ2lpO8ie3QuKjV90r30512)
      'https://apps.apple.com/app/apple-store/id6738626019?pt=127417191&ct=takezou_x&mt=8',
  };

  const goToAppStore = () => {
    const targetUserNumber = String(userNumber ?? inviterUserNumber ?? '');
    const appStoreUrl = USER_NUMBER_APP_STORE_URLS[targetUserNumber] || DEFAULT_APP_STORE_URL;
    window.location.href = appStoreUrl;
  };

  const openInstallPrompt = (message?: string) => {
    setInstallPromptMessage(message || 'Swayをインストールしましょう！！');
    setShowInstallPrompt(true);
  };

  const copyCurrentUrlForSafari = () => {
    const currentUrl = window.location.href;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(currentUrl)
        .then(() => {
          setSafariGuideCopied(true);
          setTimeout(() => setSafariGuideCopied(false), 3000);
        })
        .catch(() => {});
    }
  };

  // TikTok / Instagram / Facebook / LINE などのアプリ内ブラウザ判定
  const isInAppBrowser = () => {
    const ua = (navigator.userAgent || (navigator as any).vendor || '').toLowerCase();
    return /tiktok|musical_ly|bytedance|bytelo|instagram|fban|fbav|fb_iab|line\/|twitter/.test(ua);
  };

  const handleAppLaunch = () => {
    const currentUrl = window.location.href;

    const codeToUse = resolvedInvitationCode || invitationCode;
    if (!codeToUse) {
      console.error('❌ 招待コードが取得できません');
      openInstallPrompt('招待コードの取得に失敗しました。ページを再読み込みしてください。');
      return;
    }

    fetch(`${API_BASE_URL}/invitation/track_registration/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        invite_code: codeToUse,
        source: 'web',
      }),
    })
      .then((response) => response.json())
      .catch(() => {});

    // TikTok等のアプリ内ブラウザは App Store / App Clip / アプリ起動をすべて制限するため、
    // Safariで開き直す導線を案内する（Safariなら App Clip も App Store も正常に動く）
    if (isInAppBrowser()) {
      setSafariGuideCopied(false);
      setShowSafariGuide(true);
      return;
    }

    // この招待ページ自体が swaycross.com で配信されているため、https://swaycross.com/... への
    // Universal Link は iOS が発火させず（同一ドメイン遷移は対象外）、ただのページ遷移になって
    // しまう。アプリ起動は custom scheme で試み、開かなければ App Store にフォールバックする。
    const appUrl = `sway://invitation?code=${encodeURIComponent(codeToUse)}`;
    const launch = () => {
      // アプリが起動するとページが背面に回る。その場合は App Store に飛ばさない
      let appOpened = false;
      const markAppOpened = () => {
        if (document.visibilityState === 'hidden') appOpened = true;
      };
      document.addEventListener('visibilitychange', markAppOpened);

      window.location.href = appUrl;

      // app 未インストール端末では custom scheme が無視されるので App Store にフォールバック
      setTimeout(() => {
        document.removeEventListener('visibilitychange', markAppOpened);
        if (appOpened || document.visibilityState === 'hidden') return;
        goToAppStore();
      }, 1500);
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(currentUrl)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 3000);
          launch();
        })
        .catch(() => {
          launch();
        });
    } else {
      launch();
    }
  };

  const handleInstallNow = () => {
    setShowInstallPrompt(false);
    handleAppLaunch();
  };

  const handleInstallLater = () => {
    setShowInstallPrompt(false);
  };

  const handleSnsClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    openInstallPrompt('SNSのプロフィールはSwayアプリで確認できます');
  };

  const getLocationFromGeohash = async (geohash: string, signal?: AbortSignal): Promise<LocationInfo | null> => {
    const coordinates = decodeGeohash(geohash);
    if (!coordinates) return null;

    const result = await reverseGeocode(coordinates.latitude, coordinates.longitude, signal);
    if (!result || !result.label) return null;

    return {
      city: result.city || result.label,
      prefecture: result.prefecture || result.label,
      country: result.country || '日本',
    };
  };

  const decodeGeohash = (geohash: string): { latitude: number; longitude: number } | null => {
    const BASE32 = '0123456789bcdefghjkmnpqrstuvwxyz';
    let isEven = true;
    let lat = [-90.0, 90.0];
    let lon = [-180.0, 180.0];

    for (let i = 0; i < geohash.length; i++) {
      const char = geohash[i];
      const idx = BASE32.indexOf(char);
      if (idx === -1) return null;

      for (let j = 4; j >= 0; j--) {
        const bit = (idx >> j) & 1;
        if (isEven) {
          const mid = (lon[0] + lon[1]) / 2;
          if (bit === 1) {
            lon[0] = mid;
          } else {
            lon[1] = mid;
          }
        } else {
          const mid = (lat[0] + lat[1]) / 2;
          if (bit === 1) {
            lat[0] = mid;
          } else {
            lat[1] = mid;
          }
        }
        isEven = !isEven;
      }
    }

    return {
      latitude: (lat[0] + lat[1]) / 2,
      longitude: (lon[0] + lon[1]) / 2,
    };
  };
  const chunkArray = <T,>(array: T[], size: number): T[][] => {
    const chunks: T[][] = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  };

  const getSnsIcon = (snsId: string) => {
    if (snsId === 'pre_instagram' || snsId === 'instagram') {
      return (
        <img
          src={InstagramLogo}
          alt="Instagram"
          style={{
            width: '18px',
            height: '18px',
            objectFit: 'contain',
          }}
        />
      );
    } else if (snsId === 'x' || snsId === 'twitter') {
      return (
        <img
          src={XBlackLogo}
          alt="X"
          style={{
            width: '18px',
            height: '18px',
            objectFit: 'contain',
          }}
        />
      );
    } else if (snsId === 'link') {
      return (
        <img
          src={Link}
          alt="Link"
          style={{
            width: '18px',
            height: '18px',
            objectFit: 'contain',
          }}
        />
      );
    }
    return '🔗';
  };

  const getSnsUrl = (link: SnsLink) => {
    if (link.sns_id === 'pre_instagram' || link.sns_id === 'instagram') {
      return `https://instagram.com/${link.sns_user_id}`;
    } else if (link.sns_id === 'x' || link.sns_id === 'twitter') {
      return `https://x.com/${link.sns_user_id}`;
    } else if (link.sns_id === 'link') {
      return link.sns_user_id;
    }
    return '#';
  };

  const formatBirthday = (birthday: string | null) => {
    if (!birthday) return null;
    
    // ドット区切り（2002.4.29）の場合
    if (birthday.includes('.')) {
      const parts = birthday.split('.');
      if (parts.length === 3) {
        return `${parseInt(parts[1])}/${parseInt(parts[2])}`;
      }
    }
    
    // ハイフン区切り（2008-10-2）の場合
    if (birthday.includes('-')) {
      const parts = birthday.split('-');
      if (parts.length === 3) {
        return `${parseInt(parts[1])}/${parseInt(parts[2])}`;
      }
    }
    
    return birthday;
  };

  const handleTrackInteraction = (message?: string) => {
    openInstallPrompt(message || '音楽の詳細はSwayアプリで楽しめます');
  };

  const handleKeyboardActivate = (
    event: React.KeyboardEvent<HTMLDivElement | HTMLAnchorElement>,
    action: () => void
  ) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
  };

  const getTrackCoordinates = (track: Track): { latitude: number; longitude: number } | null => {
    if (typeof track.latitude === 'number' && typeof track.longitude === 'number') {
      return { latitude: track.latitude, longitude: track.longitude };
    }

    if (track.geohash) {
      return decodeGeohash(track.geohash);
    }

    return null;
  };

  const reverseGeocode = async (
    latitude: number,
    longitude: number,
    signal?: AbortSignal
  ): Promise<ReverseGeocodeResult | null> => {
    const buildLabel = (city?: string, prefecture?: string, fallback?: string | null) => {
      if (city && prefecture) return `${prefecture} ${city}`;
      return city || prefecture || fallback || null;
    };

    try {
      const nominatimParams = new URLSearchParams({
        format: 'json',
        lat: String(latitude),
        lon: String(longitude),
        'accept-language': 'ja',
        addressdetails: '1',
      });
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?${nominatimParams.toString()}`,
        {
          signal,
          headers: {
            'User-Agent': 'SwayInvitationPage/1.0',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.address) {
          const address = data.address;
          const city =
            address.city ||
            address.town ||
            address.village ||
            address.municipality ||
            address.suburb ||
            address.city_district ||
            address.district;

          const prefecture = address.state || address.province || address.prefecture;
          const country = address.country;
          const displayName = data.display_name as string | undefined;
          const label = buildLabel(city, prefecture, displayName || null);
          if (label) return { label, city: city || undefined, prefecture: prefecture || undefined, country };
        }
      }
    } catch (error) {
      console.warn('Nominatim reverse geocode failed (will fallback)', error);
    }

    try {
      const response = await fetch(
        `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=ja`,
        { signal }
      );

      if (!response.ok) return null;
      const data = await response.json();

      const city = data.city || data.locality || data.localityInfo?.administrative?.[0]?.name;
      const prefecture = data.principalSubdivision || data.state || data.region;
      const displayName = data.localityInfo?.informative?.[0]?.name || data.countryName;

      const label = buildLabel(city, prefecture, displayName || null);
      return {
        label,
        city: city || undefined,
        prefecture: prefecture || undefined,
        country: data.countryName,
      };
    } catch (error) {
      console.warn('Fallback reverse geocode failed', error);
      return null;
    }
  };
  const parseDate = (dateLike?: string | number | { seconds: number; nanoseconds?: number }) => {
    if (!dateLike) return null;
    if (typeof dateLike === 'string') {
      const parsed = new Date(dateLike);
      return isNaN(parsed.getTime()) ? null : parsed;
    }

    if (typeof dateLike === 'number') {
      const parsed = new Date(dateLike);
      return isNaN(parsed.getTime()) ? null : parsed;
    }

    if (typeof dateLike === 'object' && 'seconds' in dateLike) {
      return new Date(dateLike.seconds * 1000 + (dateLike.nanoseconds || 0) / 1_000_000);
    }

    return null;
  };

  const formatRelativeTime = (dateLike?: string | number | { seconds: number; nanoseconds?: number }) => {
    const parsed = parseDate(dateLike);
    if (!parsed) return '時間不明';

    const now = new Date();
    const diffMs = now.getTime() - parsed.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMinutes < 1) return 'たった今';
    if (diffMinutes < 60) return `${diffMinutes}分前`;
    if (diffHours < 24) return `${diffHours}時間前`;
    return `${diffDays}日前`;
  };

  const getLocationLabel = (index: number) => recentlyPlayedLocations[index];

  if (isLoading) {
    return (
      <div
        style={{
          width: '100%',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#0a0a0a',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '20px',
          }}
        >
          <img
            src={SwayWhiteLogo}
            alt="Sway Logo"
            style={{
              height: '32px',
              width: 'auto',
              opacity: 0.9,
              animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
            }}
          />
          <div
            style={{
              fontSize: '14px',
              fontWeight: '600',
              color: 'rgba(255, 255, 255, 0.6)',
              letterSpacing: '0.02em',
            }}
          >
            読み込み中...
          </div>
          <style>
            {`
              @keyframes pulse {
                0%, 100% {
                  opacity: 1;
                }
                50% {
                  opacity: 0.4;
                }
              }
            `}
          </style>
        </div>
      </div>
    );
  }

  if (errorKind) {
    return (
      <div
        style={{
          width: '100%',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#0a0a0a',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          padding: '20px',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '24px',
            maxWidth: '400px',
            textAlign: 'center',
          }}
        >
          <img
            src={SwayWhiteLogo}
            alt="Sway Logo"
            style={{
              height: '32px',
              width: 'auto',
              opacity: 0.6,
            }}
          />
          
          <div
            style={{
              fontSize: '48px',
              opacity: 0.3,
            }}
          >
            😔
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            <div
              style={{
                fontSize: '18px',
                fontWeight: '700',
                color: '#fff',
                letterSpacing: '-0.01em',
              }}
            >
              {errorKind === 'invalid'
                ? 'この招待リンクは現在ご利用いただけません'
                : '通信に失敗しました'}
            </div>

            <div
              style={{
                fontSize: '14px',
                color: 'rgba(255, 255, 255, 0.65)',
                lineHeight: 1.6,
              }}
            >
              {errorKind === 'invalid'
                ? 'リンクが変更されたか、招待した方のアカウントが見つかりませんでした。招待した方にリンクの再送をお願いしてください。'
                : '電波の良い場所でもう一度お試しください。'}
            </div>
          </div>

          {/* 招待者が解決できないときは再読み込みしても直らないのでボタンを出さない */}
          {errorKind === 'network' && (
            <button
              onClick={() => setRetryCount((count) => count + 1)}
              style={{
                padding: '12px 32px',
                borderRadius: '24px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                fontSize: '14px',
                fontWeight: '600',
                background: 'rgba(255, 255, 255, 0.08)',
                color: '#fff',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.12)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
              }}
            >
              再読み込み
            </button>
          )}

          <button
            onClick={goToAppStore}
            style={{
              padding: '10px 24px',
              borderRadius: '20px',
              border: 'none',
              fontSize: '13px',
              fontWeight: '600',
              background: 'rgb(4, 177, 248)',
              color: '#fff',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            Swayアプリをダウンロード
          </button>

          {/* 報告のスクリーンショット1枚で原因を切り分けるための識別子 */}
          {errorDetail && (
            <div
              style={{
                fontSize: '11px',
                color: 'rgba(255, 255, 255, 0.25)',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              }}
            >
              {errorDetail}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        minHeight: '100vh',
        position: 'relative',
        backgroundColor: '#0a0a0a',
        color: themeColors.primary,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        overflowX: 'hidden',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '430px',
          minHeight: '100vh',
          margin: '0 auto',
          position: 'relative',
          color: themeColors.primary,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          overflowX: 'hidden',
          border: typeof window !== 'undefined' && window.innerWidth > 440 ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
        }}
      >
        <div
          style={{
            backgroundColor: themeColors.headerBg,
            backdropFilter: 'blur(20px)',
            padding: '10px 12px',
            borderBottom: `1px solid ${themeColors.border}`,
            position: 'relative',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              position: 'relative',
              gap: '8px',
            }}
          >
            <div style={{ width: '72px' }}></div>

            <img
              src={SwayWhiteLogo}
              alt="Sway Logo"
              style={{
                height: '22px',
                width: 'auto',
                filter: 'drop-shadow(0 4px 12px rgba(4, 177, 248, 0.1))',
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
              }}
            />

            <button
              onClick={handleAppLaunch}
              style={{
                padding: '6px 12px',
                borderRadius: '20px',
                border: 'none',
                fontSize: '10px',
                fontWeight: 700,
                letterSpacing: '0.05em',
                background: themeColors.buttonBg,
                color: themeColors.buttonText,
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                whiteSpace: 'nowrap',
                textTransform: 'none',
                boxShadow: '0 4px 12px rgba(255, 255, 255, 0.15)',
              }}
            >
              友達登録
            </button>
          </div>
        </div>

        <div ref={scrollContainerRef} style={{ overflowY: 'visible', height: 'auto' }}>
          {!isLoading && (
            <div
              style={{
                padding: '8px 70px 8px',
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'flex-start',
                }}
              >
                <div
                  style={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    marginLeft: '-4px',
                    alignItems: 'center',
                  }}
                >
                  {inviterIconUrl ? (
                    <div
                      style={{
                        width: '100px',
                        height: '100px',
                        borderRadius: '50%',
                        overflow: 'hidden',
                        boxShadow: `0 8px 24px rgba(4, 177, 248, 0.2)`,
                        border: `2px solid ${themeColors.border}`,
                      }}
                    >
                      <img
                        src={inviterIconUrl}
                        alt={inviterNickname}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    </div>
                  ) : (
                    <div
                      style={{
                        width: '100px',
                        height: '100px',
                        borderRadius: '50%',
                        backgroundColor: themeColors.cardBg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: themeColors.secondary,
                        fontSize: '10px',
                        border: `2px solid ${themeColors.border}`,
                      }}
                    >
                      情報なし
                    </div>
                  )}
                </div>

                <div
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    paddingTop: '4px',
                    paddingLeft: '12px'
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: '2px',
                      overflow: 'hidden',
                      width: '100%',
                    }}
                  >
                    <div
                      style={{
                        marginTop: '10px',
                        fontSize: '18px',
                        fontWeight: '800',
                        color: themeColors.primary,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: 'fit-content',
                      }}
                    >
                      {inviterNickname}
                    </div>
                    {inviterUserNumber !== null && (
                      <div
                        style={{
                          fontSize: '12px',
                          fontWeight: '600',
                          color: themeColors.secondary,
                          whiteSpace: 'nowrap',
                          flexShrink: 0,
                        }}
                      >
                        @{inviterUserNumber}
                      </div>
                    )}
                  </div>

                  {favoritePlaceName && (
                    <div
                      style={{
                        marginTop: '-9px',
                        fontSize: '10px',
                        fontWeight: '800',
                        color: themeColors.secondary,
                      }}
                    >
                      よくいる場所: {favoritePlaceName}
                    </div>
                  )}
                    <div
                      style={{
                        display: 'flex',
                        gap: '2px',
                        marginTop: '-6px',
                        flexWrap: 'nowrap',
                        alignItems: 'center',
                      }}
                    >
                      {mbti && (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '2px 6px',
                            borderRadius: '16px',
                            border: `1px solid ${themeColors.borderupdate}`,
                            fontSize: '10px',
                            fontWeight: '600',
                            color: themeColors.primary,
                            backgroundColor: themeColors.cardBg,
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                          }}
                        >
                          {mbti}
                        </div>
                      )}
                      {formatBirthday(birthday) && (
                        <div
                          style={{
                            padding: '2px 4px',
                            borderRadius: '16px',
                            border: `1px solid ${themeColors.borderupdate}`,
                            fontSize: '10px',
                            fontWeight: '600',
                            color: themeColors.primary,
                            backgroundColor: themeColors.cardBg,
                            display: 'flex',
                            alignItems: 'center',
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                          }}
                        >
                          <span style={{ transform: 'scale(0.70)', display: 'inline-block',lineHeight: 1 }}>🎈</span>
                          {formatBirthday(birthday)}
                        </div>
                      )}
                    </div>

                    {/* SNSアイコン（MBTIと誕生日の下に配置） */}
                    {(() => {
                      const snsOrder = ['instagram', 'x', 'link'];
                      
                      const snsData = snsOrder.map(snsId => {
                        if (snsId === 'instagram') {
                          return snsLinks.find(link => link.sns_id === 'instagram' || link.sns_id === 'pre_instagram') || null;
                        }
                        if (snsId === 'x') {
                          return snsLinks.find(link => link.sns_id === 'x' || link.sns_id === 'twitter') || null;
                        }
                        return snsLinks.find(link => link.sns_id === snsId) || null;
                      });
                      
                      // 登録済みのSNSだけフィルタリング（型ガード付き）
                      const activeSns = snsData
                        .map((link, index) => ({ link, snsId: snsOrder[index] }))
                        .filter((item): item is { link: SnsLink; snsId: string } => item.link !== null);
                      
                      // 登録済みSNSがない場合は何も表示しない
                      if (activeSns.length === 0) return null;
                      
                      return (
                        <div
                          style={{
                            display: 'flex',
                            gap: '0px',
                            marginTop: '-8px'
                          }}
                        >
                          {activeSns.map((item, index) => (
                            <a
                              key={index}
                              href={getSnsUrl(item.link)}
                              target="_blank"
                              rel="noreferrer"
                              onClick={handleSnsClick}
                              onKeyDown={(event) => handleKeyboardActivate(event, () => openInstallPrompt())}
                              style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '7px',
                                border: `1px solid rgba(0, 0, 0, 0.1)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '14px',
                                textDecoration: 'none',
                                transition: 'transform 0.2s',
                                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
                                cursor: 'pointer',
                              }}
                              aria-label={`${item.snsId}リンクを開く`}
                            >
                              {getSnsIcon(item.snsId)}
                            </a>
                          ))}
                        </div>
                      );
                    })()}

                  {locationInfo && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        color: themeColors.secondary,
                        fontSize: '12px',
                        marginTop: '4px',
                      }}
                    >
                      <span>{locationLoading ? '場所を取得中…' : `${locationInfo.prefecture} ${locationInfo.city}`}</span>
                    </div>
                  )}
                </div>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto 1fr', // 変更: 中央を auto に
                  width: '100%',
                  gap: '16px',
                  alignItems: 'center', // 追加
                }}
              >
                {/* Cross - 左 */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                  }}
                >
                  <div
                    style={{
                      fontSize: '22px',
                      fontWeight: '800',
                      color: themeColors.primary,
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {crossCount !== null ? crossCount : 0}
                  </div>
                  <div
                    style={{
                      fontSize: '10px',
                      fontWeight: '600',
                      marginTop: '-6px',
                      color: themeColors.secondary,
                    }}
                  >
                    cross
                  </div>
                </div>

                {/* react - 中央（画面中心） */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                  }}
                >
                  <div
                    style={{
                      fontSize: '22px',
                      fontWeight: '800',
                      color: themeColors.primary,
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {reactCount !== null ? reactCount : 0}
                  </div>
                  <div
                    style={{
                      fontSize: '10px',
                      fontWeight: '600',
                      marginTop: '-6px',
                      color: themeColors.secondary,
                    }}
                  >
                    react
                  </div>
                </div>

                {/* follower - 右 */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '4px',
                  }}
                >
                  <div
                    style={{
                      fontSize: '22px',
                      fontWeight: '800',
                      color: themeColors.primary,
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {followerCount !== null ? followerCount : 0}
                  </div>
                  <div
                    style={{
                      fontSize: '10px',
                      fontWeight: '600',
                      marginTop: '-6px',
                      color: themeColors.secondary,
                    }}
                  >
                    follower
                  </div>
                </div>
              </div>
            </div>
          )}
          {recentlyPlayed && recentlyPlayed.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0 12px',
                  marginBottom: '6px',
                }}
              >
                <h3
                  style={{
                    fontSize: '15px',
                    fontWeight: '800',
                    margin: 0,
                    color: themeColors.primary,
                    letterSpacing: '-0.02em',
                  }}
                >
                  recently played
                </h3>
                <span
                  style={{
                    marginLeft: '6px',
                    fontSize: '18px',
                    fontWeight: '600',
                    color: 'rgba(255, 255, 255, 0.4)',
                  }}
                >
                  ›
                </span>
              </div>

              <div
                style={{
                  display: 'flex',
                  overflowX: 'auto',
                  gap: '0px',
                  padding: '0',
                  scrollSnapType: 'x mandatory',
                  WebkitOverflowScrolling: 'touch',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                }}
              >
                {chunkArray(recentlyPlayed, 3).slice(0, 2).map((chunk, chunkIndex) => (
                  <div
                    key={chunkIndex}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      minWidth: '90%',
                      scrollSnapAlign: 'start',
                      paddingLeft: '12px',
                      paddingRight: '12px',
                    }}
                  >
                    {chunk.map((track, index) => {
                      if (!track || !track.track_title || !track.track_artwork_url) {
                        console.warn('Invalid track data:', track);
                        return null;
                      }
                      const globalIndex = chunkIndex * 3 + index;
                      const isLockedItem = globalIndex >= 2;
                      const locationLabel = getLocationLabel(globalIndex);
                      return (
                        <div
                          key={`${chunkIndex}-${index}`}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px',
                              padding: '2px 12px 4px 0px',
                              cursor: 'pointer',
                            }}
                            role="button"
                            tabIndex={0}
                            onClick={() =>
                              handleTrackInteraction(
                                isLockedItem
                                  ? 'Recently Playedの続きはSwayでチェック!'
                                  : '音楽の詳細はSwayで楽しもう!'
                              )
                            }
                            onKeyDown={(event) =>
                              handleKeyboardActivate(event, () =>
                                handleTrackInteraction(
                                  isLockedItem
                                    ? 'Recently Playedの続きはSwayでチェック!'
                                    : '音楽の詳細はSwayで楽しもう!'
                                )
                              )
                            }
                          >
                            <div
                              style={{
                                width: '56px',
                                height: '56px',
                                flexShrink: 0,
                                borderRadius: '6px',
                                overflow: 'hidden',
                                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                              }}
                            >
                              <img
                                src={track.track_artwork_url}
                                alt={track.track_title || 'Track artwork'}
                                loading="lazy"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                }}
                              />
                            </div>
                            <div style={{ flex: 1, overflow: 'hidden' }}>
                              <div
                                style={{
                                  fontSize: '13.5px',
                                  fontWeight: '600',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  color: themeColors.primary,
                                  marginBottom: '3px',
                                  lineHeight: '1.3',
                                }}
                              >
                                {track.track_title}
                              </div>
                              <div
                                style={{
                                  fontSize: '11px',
                                  fontWeight: '400',
                                  color: 'rgba(255, 255, 255, 0.6)',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  lineHeight: '1.3',
                                }}
                              >
                                {track.track_artist || 'Unknown Artist'}
                              </div>
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-end',
                                gap: '2px',
                                minWidth: '96px',
                              }}
                            >
                              <span
                                style={{
                                  fontSize: '10px',
                                  fontWeight: 700,
                                  color: themeColors.secondary,
                                }}
                              >
                                {formatRelativeTime(track.created_at)}
                              </span>
                              {locationLabel && (
                                <span
                                  style={{
                                    fontSize: '10px',
                                    color: themeColors.secondary,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  @ {locationLabel}
                                </span>
                              )}
                            </div>
                          </div>
                          
                          {index < chunk.length - 1 && (
                            <div
                              style={{
                                height: '0.5px',
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                marginLeft: '68px',
                                marginRight: '12px',
                              }}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
                {recentlyPlayed.length > 6 && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      minWidth: '20%',
                      scrollSnapAlign: 'start',
                      paddingRight: '25px',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '12px',
                        cursor: 'pointer',
                        padding: '40px 0',
                      }}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        handleTrackInteraction('Recently Playedの続きはSwayでチェック!');
                      }}
                      onKeyDown={(event) =>
                        handleKeyboardActivate(event, () => {
                          handleTrackInteraction('Recently Playedの続きはSwayでチェック!');
                        })
                      }
                    >
                      <div
                        style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                        }}
                      >
                        <span style={{ fontSize: '16px', color: '#000' }}>→</span>
                      </div>
                      <span
                        style={{
                          fontSize: '12px',
                          fontWeight: '700',
                          color: themeColors.primary,
                        }}
                      >
                        もっと見る
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {((favoriteSongs && favoriteSongs.length > 0) || (playlists && playlists.length > 0)) && (
            <div style={{ padding: '0 12px', marginBottom: '28px' }}>
              <div
                style={{
                  display: 'flex',
                  gap: '16px',
                  marginTop: '-4px',
                  flexDirection: 'column',
                }}
              >
                <div
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    zIndex: -1,
                    background: '#11181C',
                  }}
                />
                {favoriteSongs.length > 0 && (
                  <div style={{ width: '100%' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '8px',
                      }}
                    >
                      <h3
                        style={{
                          fontSize: '15px',
                          fontWeight: '800',
                          margin: 0,
                          color: themeColors.primary,
                          letterSpacing: '-0.02em',
                        }}
                      >
                        favorite songs
                      </h3>
                      <span
                        style={{
                          marginLeft: '6px',
                          fontSize: '20px',
                          color: 'rgba(255, 255, 255, 0.4)',
                          fontWeight: '600',
                        }}
                      >
                        ›
                      </span>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        overflowX: 'auto',
                        gap: '8px',
                        padding: '0 4px',
                        paddingRight: favoriteSongs.length >= 5 ? '0' : '4px',
                        scrollSnapType: 'x mandatory',
                        WebkitOverflowScrolling: 'touch',
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                      }}
                    >
                      {chunkArray(favoriteSongs, 2).map((chunk, chunkIndex) => (
                        <div
                          key={chunkIndex}
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: favoriteSongs.length >= 5 ? '7px' : '8px',
                            minWidth: favoriteSongs.length >= 5 ? '158px' : '180px',
                            scrollSnapAlign: 'start',
                          }}
                        >
                          {chunk.map((track, index) => {
                            const globalIndex = chunkIndex * 2 + index;
                            const isLockedFavorite = globalIndex >= 3;
                            const imageSize = favoriteSongs.length >= 5 ? '47px' : '52px';
                            const titleFontSize = favoriteSongs.length >= 5 ? '10px' : '11px';
                            const artistFontSize = favoriteSongs.length >= 5 ? '9px' : '10px';
                            const borderRadius = favoriteSongs.length >= 5 ? '11px' : '12px';
                            const padding = favoriteSongs.length >= 5 ? '7px 9px' : '8px 10px';
                            const gap = favoriteSongs.length >= 5 ? '9px' : '10px';
                            
                            return (
                              <div
                                key={`fav-${index}`}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: gap,
                                  backgroundColor: 'rgba(40, 40, 40, 0.5)',
                                  borderRadius: borderRadius,
                                  padding: padding,
                                  border: 'none',
                                  cursor: 'pointer',
                                }}
                                role="button"
                                tabIndex={0}
                                onClick={() =>
                                  handleTrackInteraction(
                                    isLockedFavorite
                                      ? 'お気に入りの続きはSwayアプリで楽しもう！'
                                      : '音楽の詳細はSwayアプリで楽しめます'
                                  )
                                }
                                onKeyDown={(event) =>
                                  handleKeyboardActivate(event, () =>
                                    handleTrackInteraction(
                                      isLockedFavorite
                                        ? 'お気に入りの続きはSwayアプリで楽しもう！'
                                        : '音楽の詳細はSwayアプリで楽しめます'
                                    )
                                  )
                                }
                              >
                                <div
                                  style={{
                                    width: imageSize,
                                    height: imageSize,
                                    flexShrink: 0,
                                    borderRadius: favoriteSongs.length >= 5 ? '7px' : '8px',
                                    overflow: 'hidden',
                                  }}
                                >
                                  <img
                                    src={track.track_artwork_url}
                                    alt={track.track_title}
                                    loading="lazy"
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                    }}
                                  />
                                </div>
                                <div style={{ flex: 1, overflow: 'hidden' }}>
                                  <div
                                    style={{
                                      fontSize: titleFontSize,
                                      fontWeight: '700',
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      color: themeColors.primary,
                                      marginBottom: '4px',
                                    }}
                                  >
                                    {track.track_title}
                                  </div>
                                  <div
                                    style={{
                                      fontSize: artistFontSize,
                                      color: themeColors.secondary,
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                    }}
                                  >
                                    {track.track_artist}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {playlists.length > 0 && (
                  <div style={{ width: '100%' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '8px',
                      }}
                    >
                      <h3
                        style={{
                          fontSize: '15px',
                          fontWeight: '800',
                          margin: 0,
                          color: themeColors.primary,
                          letterSpacing: '-0.02em',
                        }}
                      >
                        Playlists
                      </h3>
                      <span
                        style={{
                          marginLeft: '6px',
                          fontSize: '20px',
                          color: 'rgba(255, 255, 255, 0.4)',
                          fontWeight: '600',
                        }}
                      >
                        ›
                      </span>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        overflowX: 'auto',
                        gap: '10px',
                        padding: '0 4px',
                        scrollSnapType: 'x mandatory',
                        WebkitOverflowScrolling: 'touch',
                        scrollbarWidth: 'none',
                        msOverflowStyle: 'none',
                      }}
                    >
                      {playlists.map((playlist, index) => {
                        const isAppleMusic = playlist.platform.toLowerCase().includes('apple');
                        const shouldUseArtwork =
                          isAppleMusic &&
                          !!playlist.artwork_url &&
                          playlist.artwork_url.startsWith('https://is1-ssl.mzstatic.com/');

                        return (
                          <div
                            key={`playlist-${index}`}
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              minWidth: '140px',
                              scrollSnapAlign: 'start',
                              cursor: 'pointer',
                            }}
                            role="button"
                            tabIndex={0}
                            onClick={() => openInstallPrompt('プレイリストの続きはSwayで楽しもう！')}
                            onKeyDown={(event) =>
                              handleKeyboardActivate(event, () =>
                                openInstallPrompt('プレイリストの続きはSwayで楽しもう！')
                              )
                            }
                          >
                            <div
                              style={{
                                position: 'relative',
                                width: '140px',
                                height: '140px',
                                overflow: 'hidden',
                                backgroundColor: 'rgba(40, 40, 40, 0.6)',
                                borderRadius: '12px',
                                marginBottom: '8px',
                              }}
                            >
                              {shouldUseArtwork ? (
                                <img
                                  src={playlist.artwork_url as string}
                                  alt={playlist.title}
                                  loading="lazy"
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                  }}
                                />
                              ) : isAppleMusic ? (
                                <img
                                  src={AppleMusicIcon}
                                  alt="Apple Music"
                                  loading="lazy"
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                  }}
                                />
                              ) : playlist.artwork_url ? (
                                <img
                                  src={playlist.artwork_url}
                                  alt={playlist.title}
                                  loading="lazy"
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: '2px',
                                    padding: '2px',
                                    backgroundColor: '#0a0a0a',
                                  }}
                                >
                                  {playlist.top_tracks?.slice(0, 4).map((track, i) => (
                                    <img
                                      key={i}
                                      src={track.track_artwork_url}
                                      alt={track.track_title}
                                      loading="lazy"
                                      style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                      }}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>

                            <div
                              style={{
                                fontSize: '11px',
                                fontWeight: '700',
                                color: themeColors.primary,
                                marginBottom: '3px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {playlist.title}
                            </div>

                            <div
                              style={{
                                fontSize: '10px',
                                color: themeColors.secondary,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {playlist.platform}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {resolvedInvitationCode && (
        <button
          onClick={() => copyToClipboard(resolvedInvitationCode)}
          style={{
            position: 'fixed',
            left: '50%',
            transform: 'translateX(-50%)',
            bottom: 'calc(20px + env(safe-area-inset-bottom))',
            zIndex: 1600,
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 18px',
            borderRadius: '999px',
            border: '1px solid rgba(255,255,255,0.15)',
            background: copied
              ? 'rgba(34,197,94,0.95)'
              : 'rgba(17,24,28,0.92)',
            color: '#ffffff',
            fontSize: '13px',
            fontWeight: 700,
            letterSpacing: '0.02em',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            boxShadow: '0 10px 28px rgba(0,0,0,0.5)',
            cursor: 'pointer',
            transition: 'background 0.2s ease-out',
            maxWidth: 'calc(100vw - 24px)',
            whiteSpace: 'nowrap',
            flexWrap: 'nowrap',
            wordBreak: 'keep-all',
            overflowWrap: 'normal',
            boxSizing: 'border-box',
          }}
        >
          <span style={{ fontSize: '16px', lineHeight: 1, flexShrink: 0 }}>🎟</span>
          {copied ? (
            <span style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>コピーしました</span>
          ) : (
            <>
              <span style={{ opacity: 0.8, whiteSpace: 'nowrap', flexShrink: 0 }}>招待コード:</span>
              <span
                style={{
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                {resolvedInvitationCode}
              </span>
              <span style={{ fontSize: '14px', opacity: 0.7, flexShrink: 0 }}>⧉</span>
            </>
          )}
        </button>
      )}

      {showInstallPrompt && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(135deg, rgba(7,12,20,0.85), rgba(10,10,10,0.8))',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            zIndex: 2000,
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '380px',
              background: 'rgba(20, 24, 32, 0.9)',
              borderRadius: '22px',
              padding: '24px 20px 18px',
              boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
              textAlign: 'center',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            <div
              style={{
                width: '92px',
                height: '92px',
                borderRadius: '22px',
                margin: '0 auto 18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img
                src={SwayLogo}
                alt="Sway"
                style={{
                  width: '92px',
                  height: '92px',
                  borderRadius: '18px',
                  objectFit: 'cover',
                  filter: 'drop-shadow(0 10px 20px rgba(4, 177, 248, 0.38))',
                }}
              />
            </div>
            <div
              style={{
                fontSize: '17px',
                fontWeight: 800,
                marginBottom: '8px',
                lineHeight: 1.5,
                letterSpacing: '-0.01em',
              }}
            >
              Swayをインストールして続きへ
            </div>
            <div
              style={{
                fontSize: '13px',
                lineHeight: 1.7,
                color: 'rgba(255,255,255,0.75)',
                marginBottom: '18px',
              }}
            >
              {installPromptMessage}
            </div>
            <button
              onClick={handleInstallNow}
              style={{
                width: '100%',
                padding: '12px 14px',
                borderRadius: '12px',
                border: 'none',
                background: ACCENT_COLOR,
                color: '#ffffff',
                fontWeight: 800,
                letterSpacing: '0.02em',
                cursor: 'pointer',
                marginBottom: '10px',
                fontSize: '14px',
              }}
            >
              Swayを開く
            </button>
            <button
              onClick={handleInstallLater}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '12px',
                border: '1px solid rgba(255,255,255,0.14)',
                background: 'rgba(255,255,255,0.04)',
                color: '#fff',
                fontWeight: 700,
                cursor: 'pointer',
                fontSize: '13px',
              }}
            >
              後で
            </button>
          </div>
        </div>
      )}

      {showSafariGuide && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(135deg, rgba(7,12,20,0.9), rgba(10,10,10,0.88))',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            zIndex: 2100,
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '380px',
              background: 'rgba(20, 24, 32, 0.95)',
              borderRadius: '22px',
              padding: '24px 20px 18px',
              boxShadow: '0 24px 60px rgba(0,0,0,0.55)',
              textAlign: 'center',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            <div
              style={{
                width: '92px',
                height: '92px',
                borderRadius: '22px',
                margin: '0 auto 18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <img
                src={SwayLogo}
                alt="Sway"
                style={{
                  width: '92px',
                  height: '92px',
                  borderRadius: '18px',
                  objectFit: 'cover',
                  filter: 'drop-shadow(0 10px 20px rgba(4, 177, 248, 0.38))',
                }}
              />
            </div>
            <div
              style={{
                fontSize: '17px',
                fontWeight: 800,
                marginBottom: '10px',
                lineHeight: 1.5,
                letterSpacing: '-0.01em',
              }}
            >
              Safariで開いてください
            </div>
            <div
              style={{
                fontSize: '13px',
                lineHeight: 1.8,
                color: 'rgba(255,255,255,0.78)',
                marginBottom: '18px',
                textAlign: 'left',
              }}
            >
              このアプリ内ブラウザではSwayをインストールできません。
              <br />
              <br />
              1. 画面右上の <b>「•••」</b> または <b>「↗」</b> をタップ
              <br />
              2. <b>「Safariで開く」</b>（または「ブラウザで開く」）を選択
            </div>
            <button
              onClick={copyCurrentUrlForSafari}
              style={{
                width: '100%',
                padding: '12px 14px',
                borderRadius: '12px',
                border: 'none',
                background: safariGuideCopied ? 'rgba(34,197,94,0.95)' : ACCENT_COLOR,
                color: '#ffffff',
                fontWeight: 800,
                letterSpacing: '0.02em',
                cursor: 'pointer',
                marginBottom: '10px',
                fontSize: '14px',
                transition: 'background 0.2s ease-out',
              }}
            >
              {safariGuideCopied ? 'リンクをコピーしました' : 'リンクをコピー'}
            </button>
            <div
              style={{
                fontSize: '11px',
                lineHeight: 1.6,
                color: 'rgba(255,255,255,0.5)',
                marginBottom: '14px',
              }}
            >
              コピーしてSafariのアドレスバーに貼り付けても開けます
            </div>
            <button
              onClick={() => setShowSafariGuide(false)}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '12px',
                border: '1px solid rgba(255,255,255,0.14)',
                background: 'rgba(255,255,255,0.04)',
                color: '#fff',
                fontWeight: 700,
                cursor: 'pointer',
                fontSize: '13px',
              }}
            >
              閉じる
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvitationPage;